/**
 * --------------------------------------------------------------------------
 * Component Name : ExpenseReports.jsx
 * Created Date   : 03-Nov-2025
 * Created By     : Sreeraj
 * Modified Date  : 05-Nov-2025
 * Modified By    : Sreeraj
 * Description    : Displays Expense Reports (Detailed or Consolidated)
 * --------------------------------------------------------------------------
 */

import React, { useEffect, useState } from "react";
import { FormControl, Box, IconButton, Switch } from "@mui/material";
import { PiMicrosoftExcelLogoLight } from "react-icons/pi";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import "./ExpenseReport.css";

import CustomYearPicker from "../../../../components/custom_year_picker/CustomYearPicker";
import CustomAutoCompleteDropdown from "../../../../components/custom_auto_complete_dropdown/CustomAutoCompleteDropdown";
import CustomTable from "../../../../components/custom_table/CustomTable";
import CustomBottomPagination from "../../../../components/custom_bottom_pagination/CustomBottomPagination";
import CustomToggleSelector from "../../../../components/custom_toggle_selector/CustomToggleSelector";
import ConsolidatedReport from "../consolidated_report/ConsolidatedReport";
import { ErrorToast, SuccessToast } from "../../../../toast/toasts";
import { CommonHelpers } from "../../../../services/commonHelpers";
import {
  get_expense_profit_report,
  get_expense_report,
} from "../../../../api/expenseReportApis";
import ExpandableView from "../../../../components/expandable_view/ExpandableView";

const months = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const ExpenseReports = () => {
  const currentMonth = new Date().getMonth() + 1;
  const formattedMonth = String(currentMonth).padStart(2, "0");
  const currentYear = new Date().getFullYear();

  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItemPerPage, setTotalItemPerPage] = useState(25);
  const [reportList, setReportList] = useState([]);
  const [isFinancialYear, setIsFinancialYear] = useState(false);
  const [totalExpense, setTotalExpense] = useState(0);
  const [reportType, setReportType] = useState("detailed");

  const [filterPayload, setFilterPayload] = useState({
    month: reportType === `detailed` ? `${currentYear}-${formattedMonth}` : "",
    year: dayjs(`${currentYear}-01-01`),
    financial_year_start: CommonHelpers.getLastCompletedFinancialYear().start,
    financial_year_end: CommonHelpers.getLastCompletedFinancialYear().end,
    filter_type: "year",
  });

  // const [filterPayload, setFilterPayload] = useState({
  //     month: "", // Default to "All" (no month)
  //     year: dayjs(`${currentYear}-01-01`),
  //     financial_year_start: "",
  //     financial_year_end: "",
  //     filter_type: "year",
  // });

  useEffect(() => {
    getExpenseReport();
  }, [pageNumber, totalItemPerPage, filterPayload, reportType]);

  const [isLoading, setIsLoading] = useState(true);

  const getExpenseReport = async () => {
    const formData = new FormData();
    formData.append("page", pageNumber);
    formData.append("pageItem_count", totalItemPerPage);

    Object.entries(filterPayload).forEach(([key, value]) => {
      if (!value) return; // ⬅ Skip empty filters
      if (reportType === "consolidated" && key === "month") return;

      if (key === "year" && value) {
        const yearValue = value.year
          ? value.year()
          : new Date(value).getFullYear();
        formData.append(key, yearValue);
      } else {
        formData.append(key, value);
      }
    });

    try {
      const response = await get_expense_report(formData);
      if (response?.data?.status) {
        setReportList(
          reportType === `detailed`
            ? response?.data?.normal_listing?.data
            : response?.data?.monthly_expenses || []
        );
       
        setTotalPages(response.data.normal_listing.last_page);
        setTotalExpense(response.data.total_amount || 0);
      } else {
        setReportList([]);
        setTotalPages(0);
        setTotalExpense(0);
      }
       setIsLoading(false);
    } catch (error) {
      setReportList([]);
      setTotalPages(0);
      setTotalExpense(0);
      ErrorToast("Something went wrong while fetching expenses");
    }
  };

  const Columns = [
    { title: "Sl. No", dataIndex: "key", key: "key" },
    { title: "Month", dataIndex: "month", key: "month" },
    { title: "Expense Head", dataIndex: "head", key: "head" },
    { title: "Amount (₹)", dataIndex: "amount", key: "amount" },
    { title: "Description", dataIndex: "description", key: "description" },
    { title: "Created By", dataIndex: "created_by", key: "created_by" },
  ];

  const dataSource = reportList.map((item, index) => ({
    key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
    month: dayjs(item.month).format("MMM YYYY") || "-",
    head: item.head || "-",
    description: item.description || "-",
    amount: item.amount || 0,
    created_by: item.created_by || "-",
  }));

  const handleFilterChange = (key, value) => {
    setFilterPayload((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPageNumber(1);
  };

  const handleToggleFinancialYear = (event) => {
    const checked = event.target.checked;
    setIsFinancialYear(checked);
    handleFilterChange("filter_type", checked ? "financial_year" : "year");
  };

  // const handleToggleFinancialYear = (event) => {
  //     const checked = event.target.checked;
  //     setIsFinancialYear(checked);

  //     if (checked) {
  //         // Get last completed financial year from helper
  //         const lastFY = CommonHelpers.getLastCompletedFinancialYear();

  //         // Automatically set the start and end of last FY
  //         setFilterPayload((prev) => ({
  //             ...prev,
  //             financial_year_start: lastFY.start,
  //             financial_year_end: lastFY.end,
  //             filter_type: "financial_year",
  //             month: "", // clear month when using financial year
  //         }));
  //     } else {
  //         // Reset to normal year filter (current year)
  //         setFilterPayload((prev) => ({
  //             ...prev,
  //             filter_type: "year",
  //             financial_year_start: "",
  //             financial_year_end: "",
  //             year: dayjs(`${new Date().getFullYear()}-01-01`),
  //         }));
  //     }

  //     setPageNumber(1);
  // };

  const financialYearOptions = CommonHelpers.getFinancialYearList(2023, 3).map(
    (fy) => ({
      label: fy.label,
      value: fy.value,
    })
  );

  return (
    <Box className="expense_report_main_container">
      {/* HEADER */}
      <div className="report-header">
        <h3 className="expense_report_title">Expense Report</h3>
        {/* <Box display="flex" alignItems="center" gap={2}>
                    <IconButton title="Download Excel">
                        <PiMicrosoftExcelLogoLight size={25} color="green" />
                    </IconButton>
                </Box> */}
      </div>

      {/* TOGGLE BETWEEN REPORT TYPES */}
      <CustomToggleSelector
        options={[
          { label: "Detailed Report", value: "detailed" },
          { label: "Consolidated Report", value: "consolidated" },
        ]}
        selectedValue={reportType}
        onChange={(value) => setReportType(value)}
      />

      {/* FILTERS */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "20% 20% 20% 20% auto",
          alignItems: "center",
          gap: 2,
          marginY: 2,
        }}
      >
        {/* DETAILED REPORT => Month + Year filters */}
        {reportType === "detailed" && !isFinancialYear && (
          <>
            {/* <FormControl>
                            <CustomAutoCompleteDropdown
                                allowClear={false}
                                readOnly={true}
                                options={[
                                    { label: "All", value: "" },
                                    ...months.map((m) => ({
                                        label: m.label,
                                        value: `${filterPayload.year?.year?.() ?? dayjs().year()}-${m.value}`,
                                    })),
                                ]}
                                onChange={(e, newValue) => handleFilterChange("month", newValue?.value ?? "")}
                                value={
                                    filterPayload.month
                                        ? months
                                            .map((m) => ({
                                                label: m.label,
                                                value: `${filterPayload.year?.year?.() ?? dayjs().year()}-${m.value}`,
                                            }))
                                            .find((m) => m.value === filterPayload.month)
                                        : { label: "All", value: "" } //  default to "All"
                                }
                            />
                        </FormControl> */}
            <FormControl>
              <CustomAutoCompleteDropdown
                allowClear={false}
                readOnly={true}
                options={months.map((m) => ({
                  label: m.label,
                  value: `${filterPayload.year.year()}-${m.value}`,
                }))}
                onChange={(e, newValue) =>
                  handleFilterChange("month", newValue?.value)
                }
                value={months
                  .map((m) => ({
                    label: m.label,
                    value: `${filterPayload.year.year()}-${m.value}`,
                  }))
                  .find((m) => m.value === filterPayload.month)}
              />
            </FormControl>

            <FormControl>
              <CustomYearPicker
                readOnly={true}
                views={["year"]}
                maxDate={dayjs().endOf("year")}
                value={filterPayload.year}
                onChange={(year) => handleFilterChange("year", year)}
              />
            </FormControl>
          </>
        )}

        {/* CONSOLIDATED REPORT => Only Year filter */}
        {reportType === "consolidated" && !isFinancialYear && (
          <FormControl>
            <CustomYearPicker
              readOnly={true}
              views={["year"]}
              maxDate={dayjs().endOf("year")}
              value={filterPayload.year}
              onChange={(year) => handleFilterChange("year", year)}
            />
          </FormControl>
        )}

        {/* Financial Year dropdown (for both report types) */}
        {isFinancialYear && (
          <FormControl>
            <CustomAutoCompleteDropdown
              allowClear={false}
              readOnly={true}
              options={financialYearOptions}
              onChange={(e, newValue) => {
                handleFilterChange(
                  "financial_year_start",
                  newValue?.value.start
                );
                handleFilterChange("financial_year_end", newValue?.value.end);
              }}
              value={
                financialYearOptions.find(
                  (fy) =>
                    fy.value.start === filterPayload.financial_year_start &&
                    fy.value.end === filterPayload.financial_year_end
                ) || null
              }
            />
          </FormControl>
        )}

        {/* Toggle */}
        <FormControl>
          <div className="expense_report_financial_year_toggle_container">
            <Switch
              checked={isFinancialYear}
              onChange={handleToggleFinancialYear}
            />
            <p>Financial Year</p>
          </div>
        </FormControl>
      </Box>

      {/* REPORT TABLE */}
      <div>
        {reportType === "detailed" ? (
          <>
            <CustomTable
              columns={Columns}
              rows={dataSource}
              loading={isLoading}
            />
            <CustomBottomPagination
              pageFrom="Expense_report"
              current_page={pageNumber}
              total_page_count={totalPages}
              on_change_value={setPageNumber}
              on_page_count_update={setTotalItemPerPage}
              total_items_per_page={totalItemPerPage}
            />
          </>
        ) : (
          <ConsolidatedReport
            setReportType={setReportType}
            apiResponse={reportList}
            year={filterPayload?.year}
            onMonthSelect={(selectedMonth) =>
              handleFilterChange("month", selectedMonth)
            }
          />
        )}
      </div>
      {/* SUMMARY */}
      <Box className="expense_report_summary">
        <strong>Total Expense: ₹{totalExpense}</strong>
      </Box>
    </Box>
  );
};

export default ExpenseReports;
